import type {
  AcceptedInvitationDto,
  ApiKeyDto,
  ApplicationDto,
  ApplicationListItem,
  ApplicationOverviewDto,
  BillingDto,
  BillingRedirectDto,
  ChannelDto,
  CreateApiKeyInput,
  CreateApplicationInput,
  CreateChannelInput,
  CreatedApiKeyDto,
  CreateNotificationInput,
  DeliveryDto,
  InvitationDto,
  InviteMemberInput,
  MemberDto,
  NotificationDto,
  NotificationListQuery,
  OrganizationDto,
  OrganizationOverviewDto,
  Page,
  PublicApplicationDto,
  ReceivedInvitationDto,
  SubscriberDto,
  SubscriptionStatus,
  UpdateApplicationInput,
  UpdateChannelInput,
} from "@nudgebox/contracts";
import { createHttpClient, type HttpClientOptions } from "./http.ts";

const seg = encodeURIComponent;

interface OrgParams {
  org: string;
}
interface AppParams extends OrgParams {
  app: string;
}

/**
 * Typed client for the Nudgebox HTTP API, used by the dashboard.
 * Every method maps to exactly one REST route.
 */
export function createApiClient(options: HttpClientOptions = {}) {
  const http = createHttpClient(options);
  const orgPath = (p: OrgParams) => `/v1/orgs/${seg(p.org)}`;
  const appPath = (p: AppParams) => `${orgPath(p)}/apps/${seg(p.app)}`;

  return {
    organizations: {
      list: () => http.get<{ data: OrganizationDto[] }>("/v1/orgs").then((r) => r.data),
      get: (p: OrgParams) => http.get<OrganizationDto>(orgPath(p)),
      overview: (p: OrgParams) => http.get<OrganizationOverviewDto>(`${orgPath(p)}/overview`),
      members: (p: OrgParams) => http.get<{ data: MemberDto[] }>(`${orgPath(p)}/members`).then((r) => r.data),
    },

    invitations: {
      list: (p: OrgParams) => http.get<{ data: InvitationDto[] }>(`${orgPath(p)}/invitations`).then((r) => r.data),
      create: (p: OrgParams & { input: InviteMemberInput }) =>
        http.post<InvitationDto>(`${orgPath(p)}/invitations`, { body: p.input }),
      cancel: (p: OrgParams & { invitationId: string }) =>
        http.delete(`${orgPath(p)}/invitations/${seg(p.invitationId)}`),
      /** An invitation sent to the signed-in user. */
      received: (p: { invitationId: string }) =>
        http.get<ReceivedInvitationDto>(`/v1/invitations/${seg(p.invitationId)}`),
      accept: (p: { invitationId: string }) =>
        http.post<AcceptedInvitationDto>(`/v1/invitations/${seg(p.invitationId)}/accept`),
      reject: (p: { invitationId: string }) => http.post<void>(`/v1/invitations/${seg(p.invitationId)}/reject`),
    },

    applications: {
      list: (p: OrgParams) => http.get<{ data: ApplicationListItem[] }>(`${orgPath(p)}/apps`).then((r) => r.data),
      get: (p: AppParams) => http.get<ApplicationOverviewDto>(appPath(p)),
      create: (p: OrgParams & { input: CreateApplicationInput }) =>
        http.post<ApplicationDto>(`${orgPath(p)}/apps`, { body: p.input }),
      update: (p: AppParams & { input: UpdateApplicationInput }) =>
        http.patch<ApplicationDto>(appPath(p), { body: p.input }),
      delete: (p: AppParams) => http.delete(appPath(p)),
    },

    channels: {
      list: (p: AppParams) => http.get<{ data: ChannelDto[] }>(`${appPath(p)}/channels`).then((r) => r.data),
      create: (p: AppParams & { input: CreateChannelInput }) =>
        http.post<ChannelDto>(`${appPath(p)}/channels`, { body: p.input }),
      update: (p: AppParams & { channelId: string; input: UpdateChannelInput }) =>
        http.patch<ChannelDto>(`${appPath(p)}/channels/${seg(p.channelId)}`, { body: p.input }),
      delete: (p: AppParams & { channelId: string }) => http.delete(`${appPath(p)}/channels/${seg(p.channelId)}`),
    },

    notifications: {
      list: (
        p: AppParams & {
          cursor?: string | undefined;
          limit?: number | undefined;
          view?: NotificationListQuery["view"] | undefined;
          /** Inclusive lower bound, ISO 8601. */
          from?: string | undefined;
          /** Exclusive upper bound, ISO 8601. */
          to?: string | undefined;
        },
      ) =>
        http.get<Page<NotificationDto>>(`${appPath(p)}/notifications`, {
          query: { cursor: p.cursor, limit: p.limit, view: p.view, from: p.from, to: p.to },
        }),
      get: (p: AppParams & { notificationId: string }) =>
        http.get<NotificationDto>(`${appPath(p)}/notifications/${seg(p.notificationId)}`),
      deliveries: (p: AppParams & { notificationId: string }) =>
        http
          .get<{ data: DeliveryDto[] }>(`${appPath(p)}/notifications/${seg(p.notificationId)}/deliveries`)
          .then((r) => r.data),
      create: (p: AppParams & { input: CreateNotificationInput; idempotencyKey?: string | undefined }) =>
        http.post<NotificationDto>(`${appPath(p)}/notifications`, {
          body: p.input,
          headers: p.idempotencyKey ? { "idempotency-key": p.idempotencyKey } : undefined,
        }),
      /** Withdraws a scheduled notification before it goes out. */
      cancel: (p: AppParams & { notificationId: string }) =>
        http.post<NotificationDto>(`${appPath(p)}/notifications/${seg(p.notificationId)}/cancel`),
    },

    subscribers: {
      list: (
        p: AppParams & {
          cursor?: string | undefined;
          status?: SubscriptionStatus | undefined;
          channel?: string | undefined;
        },
      ) =>
        http.get<Page<SubscriberDto>>(`${appPath(p)}/subscribers`, {
          query: { cursor: p.cursor, status: p.status, channel: p.channel },
        }),
    },

    apiKeys: {
      list: (p: OrgParams) => http.get<{ data: ApiKeyDto[] }>(`${orgPath(p)}/api-keys`).then((r) => r.data),
      create: (p: OrgParams & { input: CreateApiKeyInput }) =>
        http.post<CreatedApiKeyDto>(`${orgPath(p)}/api-keys`, { body: p.input }),
      revoke: (p: OrgParams & { keyId: string }) => http.delete(`${orgPath(p)}/api-keys/${seg(p.keyId)}`),
    },

    billing: {
      get: (p: OrgParams) => http.get<BillingDto>(`${orgPath(p)}/billing`),
      checkout: (p: OrgParams) => http.post<BillingRedirectDto>(`${orgPath(p)}/billing/checkout`),
      portal: (p: OrgParams) => http.post<BillingRedirectDto>(`${orgPath(p)}/billing/portal`),
    },

    publicApplications: {
      get: (p: { slug: string }) => http.get<PublicApplicationDto>(`/v1/public/apps/${seg(p.slug)}`),
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
